/**
 * Blog editor toolbar formatting — bold and italic.
 *
 * Two commands, one per mark, because Chrome's `readOnlyHint` and the safety
 * policy read the single `consequence` and a command's danger must not vary by
 * argument. Both toggle the mark on the current selection of the live TipTap
 * document, exactly as the toolbar buttons do, and both are `writes-config`:
 * they mutate only the in-memory buffer, never persist, and undo with one
 * Ctrl+Z. Nothing reaches our DB or the client site until the operator saves.
 */
import { z } from 'zod';
import {
  defineCommand,
  refuse,
  type CommitAuthorization,
  type Diff,
  type Receipt,
  type Refusal,
  type ResolveInput,
  type TenantId,
  type WriteDeps,
} from '../contract';

const noArgs = z.object({}).describe('No arguments; acts on the current selection.');
type NoArgs = z.infer<typeof noArgs>;

interface FormatTarget {
  readonly contentId: string;
  readonly mark: 'bold' | 'italic';
  readonly wasActive: boolean;
}

async function resolveFormat(
  input: ResolveInput<NoArgs>,
  mark: 'bold' | 'italic',
): Promise<FormatTarget | Refusal> {
  const { tenant, read } = input;
  if (!read.editorContext) {
    return refuse('This surface cannot see the blog editor, so it cannot format text.');
  }
  const ctx = read.editorContext(tenant);
  if (!ctx) {
    return refuse('No blog editor is open in this tab, so there is nothing to format.', {
      retryable: true,
    });
  }
  return {
    contentId: ctx.contentId,
    mark,
    wasActive: mark === 'bold' ? ctx.boldActive : ctx.italicActive,
  };
}

function formatDiff(target: FormatTarget, tenant: TenantId): Diff {
  return {
    table: 'blog_content',
    rowId: target.contentId,
    tenant,
    // The digest binds this exact string; `SEAM_BINDINGS.editorFormat` reproduces
    // `toggle <mark>`. A toggle has no stable `from`, so it is left null.
    changes: [{ field: 'format', from: null, to: `toggle ${target.mark}`, authorability: 'exact' }],
    reachesGeneratedContent: false,
  };
}

async function runFormat(
  target: FormatTarget,
  auth: CommitAuthorization,
  write: WriteDeps,
): Promise<Receipt | Refusal> {
  if (!write.editorFormat) {
    return refuse('This surface cannot change formatting.');
  }
  return write.editorFormat(auth, target.mark);
}

export const edToggleBold = defineCommand<NoArgs, FormatTarget>({
  name: 'ed_toggle_bold',
  section: 'editor',
  consequence: 'writes-config',
  description:
    'Toggle bold on the current selection in the blog editor, exactly like the toolbar Bold button. This is a local edit to the open document; it is not saved until you save the article.',
  args: noArgs,
  resolve: (input) => resolveFormat(input, 'bold'),
  diff: formatDiff,
  run: runFormat,
});

export const edToggleItalic = defineCommand<NoArgs, FormatTarget>({
  name: 'ed_toggle_italic',
  section: 'editor',
  consequence: 'writes-config',
  description:
    'Toggle italic on the current selection in the blog editor, exactly like the toolbar Italic button. This is a local edit to the open document; it is not saved until you save the article.',
  args: noArgs,
  resolve: (input) => resolveFormat(input, 'italic'),
  diff: formatDiff,
  run: runFormat,
});
